import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import JsonLd from "@/components/feature/JsonLd";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const positions = [
  {
    icon: "ri-flashlight-line",
    title: "快，但不抢跑",
    text: "突发第一秒就同步，但不会为了快而编。没核实的内容会明确标注「仍在讨论」，不让读者替我们承担风险。",
  },
  {
    icon: "ri-links-line",
    title: "给来龙去脉",
    text: "一条速报不只是甩个结论，我们把时间线、双方表态和前情一并排好，让第一次看到这件事的人也能看懂。",
  },
  {
    icon: "ri-scales-3-line",
    title: "留有余地",
    text: "未被官方确认的信息，用可识别描述代替全名；出现反转或澄清时，第一时间更新，不装作没发生过。",
  },
];

const principles = [
  "只整理公开可查的信息，不发布无法核实的私下内容。",
  "涉及未成年人与非公众人物时，主动做模糊处理。",
  "同一事件的不同说法同时呈现，不替读者下结论。",
  "出现新进展或反转，更新到原速报的时间线，而不是新开一条。",
  "不接受以流量为目的的情绪化标题，标题即事实范围。",
];

const voices = [
  {
    name: "追瓜的阿柚",
    role: "读者 · 3 年",
    text: "以前看八卦得在评论区拼线索，现在打开瓜圈速报基本就有一个完整版。时间线那个设计真的很省事。",
  },
  {
    name: "晚睡编辑部",
    role: "读者 · 论坛管理员",
    text: "作为每天整理讨论的人，我最看重的是它标注了「哪些还没确认」，这让我们转述的时候心里有底。",
  },
  {
    name: "不追星的林栗",
    role: "读者 · 1 年",
    text: "本来对娱乐圈没什么兴趣，但这里的写法克制，像看一篇小报道，不会满屏都是惊叹号和猜测。",
  },
];

function About() {
  useDocumentMeta({
    title: "关于我们 - 瓜圈速报｜内容原则与编辑部定位",
    description:
      "瓜圈速报是一支专注娱乐圈快讯的内容编辑团队，坚持快速同步突发与最新进展，同时明确区分已确认信息与仍在讨论的说法，为读者提供可读、可核查的来龙去脉。",
    keywords: "瓜圈速报,关于我们,内容原则,编辑部,第一手快讯",
    canonical: "https://zgdkf.cn/about",
  });

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "AboutPage",
        name: "关于我们 - 瓜圈速报",
        inLanguage: "zh-CN",
        url: "https://zgdkf.cn/about",
      },
      {
        "@type": "Organization",
        name: "瓜圈速报",
        url: "https://zgdkf.cn/",
        email: "hi@guaspeed.example.com",
        description: "专注娱乐圈突发爆料与最新进展的快讯编辑团队。",
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://zgdkf.cn/" },
          { "@type": "ListItem", position: 2, name: "关于我们" },
        ],
      },
    ],
  };

  return (
    <>
      <JsonLd data={jsonLd} />
      <SiteHeader />
      <main className="pt-24 md:pt-28">
        <section className="w-full px-4 md:px-10 pt-6">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
        </section>

        <section className="relative w-full px-4 md:px-10 pt-8 pb-14 md:pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <span className="inline-flex items-center gap-2 text-xs font-medium text-primary-600 tracking-[0.2em]">
                <span className="w-6 h-px bg-primary-500" />
                ABOUT US
              </span>
              <h1 className="mt-3 font-heading text-3xl md:text-5xl font-black text-foreground-950 leading-tight">
                我们只做一件事：
                <br />
                把瓜讲清楚
              </h1>
              <p className="mt-6 text-base leading-loose text-foreground-700">
                瓜圈速报诞生于一个很朴素的念头：吃瓜最难受的不是瓜不好吃，而是信息太碎。截图、评论、二手转述混在一起，等你好不容易拼出轮廓，话题已经翻篇了。
              </p>
              <p className="mt-4 text-base leading-loose text-foreground-700">
                所以我们把「同步速度」和「信息完整度」当成两条并行的标准线。突发时先发快讯让你不错过第一口，随后持续补充时间线、各方回应与后续动态，直到这件事有相对清楚的面貌。
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link
                  to="/category/all"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-bold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-list-check-3" />
                  </span>
                  去看全部速报
                </Link>
                <a
                  href="mailto:hi@guaspeed.example.com"
                  rel="nofollow"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-background-300 text-foreground-800 text-sm font-bold whitespace-nowrap cursor-pointer hover:border-primary-400 hover:text-primary-600 transition-colors"
                >
                  联系编辑部
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="w-full h-72 md:h-96 rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20minimal%20editorial%20desk%20flatlay%20with%20notebook%20pen%20and%20soft%20afternoon%20light%2C%20clean%20organized%20creative%20workspace%20in%20amber%20and%20cream%20tones%2C%20no%20people%2C%20editorial%20still%20life%20photography%20high%20detail&width=1000&height=1200&seq=guaspeed-about-editorial&orientation=portrait"
                  alt="瓜圈速报编辑部工作场景"
                  title="瓜圈速报编辑部 - 内容原则"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-10 py-14 md:py-20 bg-background-100">
          <h2 className="font-heading text-2xl md:text-4xl font-black text-foreground-950">我们的定位</h2>
          <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-2xl leading-relaxed">
            不做立场输出，只做信息整理。三条基本态度，决定了我们怎么写每一条速报。
          </p>
          <div className="mt-9 grid grid-cols-1 md:grid-cols-3 gap-5">
            {positions.map((item, idx) => (
              <div key={item.title} className="bg-background-50 border border-background-200 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <span className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                    <i className={`${item.icon} text-2xl`} />
                  </span>
                  <span className="font-heading text-2xl font-black text-foreground-200">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="mt-5 font-heading text-lg font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="w-full px-4 md:px-10 py-14 md:py-20 bg-background-50">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <span className="inline-flex items-center gap-2 text-xs font-medium text-accent-700 tracking-[0.2em]">
                <span className="w-6 h-px bg-accent-500" />
                PRINCIPLES
              </span>
              <h2 className="mt-3 font-heading text-2xl md:text-4xl font-black text-foreground-950 leading-tight">
                内容原则
              </h2>
              <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">
                这些规则会一直贴在编辑部的墙上。它们不保证我们永远不出错，但至少能保证每一次判断都有依据。
              </p>
              <div className="mt-8 p-5 rounded-lg bg-accent-50 border border-accent-200">
                <p className="text-sm leading-relaxed text-accent-900">
                  如果你发现某条速报与你掌握的公开信息不符，欢迎邮件指出。我们会核查后更正，并把更正记录留在原速报里。
                </p>
              </div>
            </div>

            <div className="lg:col-span-7">
              <ol className="space-y-4">
                {principles.map((text, idx) => (
                  <li
                    key={text}
                    className="flex gap-4 bg-background-50 border border-background-200 rounded-lg p-5"
                  >
                    <span className="shrink-0 w-8 h-8 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-900 font-heading text-sm font-black">
                      {idx + 1}
                    </span>
                    <p className="text-sm md:text-base leading-relaxed text-foreground-800 pt-1">{text}</p>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-10 py-14 md:py-20 bg-background-100">
          <h2 className="font-heading text-2xl md:text-4xl font-black text-foreground-950">读者怎么说</h2>
          <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-2xl leading-relaxed">
            来自后台留言与读者群的反馈，是我们调整写法最直接的依据。
          </p>
          <div className="mt-9 grid grid-cols-1 md:grid-cols-3 gap-5">
            {voices.map((voice) => (
              <blockquote key={voice.name} className="bg-background-50 border border-background-200 rounded-lg p-6 flex flex-col">
                <span className="w-8 h-8 flex items-center justify-center text-accent-500 mb-4">
                  <i className="ri-double-quotes-l text-2xl" />
                </span>
                <p className="text-sm leading-relaxed text-foreground-700 flex-1">{voice.text}</p>
                <footer className="mt-5 pt-5 border-t border-background-200 flex items-center gap-3">
                  <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 font-heading text-sm font-black">
                    {voice.name.slice(0, 1)}
                  </span>
                  <span className="flex flex-col">
                    <cite className="not-italic text-sm font-medium text-foreground-900">{voice.name}</cite>
                    <span className="text-xs text-foreground-500">{voice.role}</span>
                  </span>
                </footer>
              </blockquote>
            ))}
          </div>
        </section>

        <section className="w-full px-4 md:px-10 py-14 md:py-20 bg-background-50">
          <div className="rounded-lg border border-background-200 bg-secondary-50 p-8 md:p-12 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <h2 className="font-heading text-2xl md:text-3xl font-black text-foreground-950">想说的话，直接发给我们</h2>
              <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-700 max-w-2xl">
                线索投稿、内容纠错、转载沟通或单纯想聊聊，都可以发邮件。我们尽量在两天内回复，时间紧张时也会先看一眼。
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-4 text-sm">
                <a
                  href="mailto:hi@guaspeed.example.com"
                  rel="nofollow"
                  className="inline-flex items-center gap-2 font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-mail-line" />
                  </span>
                  hi@guaspeed.example.com
                </a>
                <span className="inline-flex items-center gap-2 text-foreground-600">
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-time-line" />
                  </span>
                  更新时段：每天 09:00 - 次日 03:00
                </span>
              </div>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end">
              <a
                href="mailto:hi@guaspeed.example.com"
                rel="nofollow"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-bold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-send-plane-line" />
                </span>
                写邮件给编辑部
              </a>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}

export default About;